import {
  Button,
  LabeledDatePickerInput,
  LabeledInput,
  LabeledSelect,
} from "@apex/component-library";
import { Box, Stack, useDisclosure } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useEffect } from "react";

import { AccountData } from "../../api/useGetAccountDetails";
import { EventCodeLabels } from "../../types";
import { getDatePickerFormattedDate } from "../../utils/dateFormatter";
import CreateCallPreviewModal from "../CreateCallPreviewModal";
import InputFieldWrapper from "../InputFieldWrapper";
import { CreateCallFormValues } from "./types";

const FIELD_WIDTH = "47.5%";
export interface CreateCallFormProps {
  accountId: string;
  accountData?: AccountData;
  setAccountId: (accountId: string) => void;
}

const CreateCallForm = ({
  accountId,
  accountData,
  setAccountId,
}: CreateCallFormProps) => {
  const {
    values,
    handleChange,
    handleBlur,
    touched,
    dirty,
    errors,
    isValid,
    resetForm,
    setValues,
    setFieldValue,
  } = useFormikContext<CreateCallFormValues>();

  useEffect(resetForm, [accountId, resetForm]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSetDate = (fieldName: string, date: string | Date) => {
    setFieldValue(fieldName, getDatePickerFormattedDate(date));
  };

  return (
    <>
      {accountData && (
        <form>
          <Stack spacing={4}>
            <InputFieldWrapper width={FIELD_WIDTH}>
              <LabeledSelect
                label="Call Type"
                name="callType"
                value={values.callType}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.callType && !!errors.callType}
                errorMessage={errors.callType}
                isRequired
                data-testid="call-type-input"
              >
                {Object.values(EventCodeLabels)
                  .filter(({ value }) => value !== "DT") // Hide deprecated DT (FEECM-17670)
                  .map(({ value, label }) => (
                    <option key={value + "-callType"} value={value}>
                      {label}
                    </option>
                  ))}
              </LabeledSelect>
            </InputFieldWrapper>
            <InputFieldWrapper width={FIELD_WIDTH}>
              <LabeledInput
                label="Call Amount"
                name="callAmountDisplay"
                isRequired
                iconLeft="$"
                isFormattedNumber
                value={values.callAmountDisplay}
                onChange={({ display, raw }) => {
                  setValues({
                    ...values,
                    callAmountDisplay: display || "",
                    callAmountRaw: raw,
                  });
                }}
                onBlur={handleBlur}
                isInvalid={
                  touched.callAmountDisplay &&
                  (!!errors.callAmountRaw || !!errors.callAmountDisplay)
                }
                errorMessage={errors.callAmountRaw || errors.callAmountDisplay}
                data-testid="call-amount-input"
              />
            </InputFieldWrapper>
            <InputFieldWrapper width={FIELD_WIDTH}>
              <LabeledDatePickerInput
                data-testid="trade-date-input"
                date={values.tradeDate}
                disableFutureDates
                errorMessage={errors.tradeDate}
                isInvalid={touched.tradeDate && !!errors.tradeDate}
                isRequired
                label="Trade Date"
                name="tradeDate"
                onBlur={handleBlur}
                onChange={(date: string | Date) => {
                  handleSetDate("tradeDate", date);
                }}
                placement="bottom"
              />
            </InputFieldWrapper>
            <InputFieldWrapper width={FIELD_WIDTH}>
              <LabeledDatePickerInput
                label="Issue Date"
                name="issueDate"
                onChange={(date: string | Date) => {
                  handleSetDate("issueDate", date);
                }}
                onBlur={handleBlur}
                date={values.issueDate}
                placement="bottom"
                isInvalid={touched.issueDate && !!errors.issueDate}
                errorMessage={errors.issueDate}
                disableFutureDates
                isRequired
                data-testid="issue-date-input"
              />
            </InputFieldWrapper>
            <InputFieldWrapper width={FIELD_WIDTH}>
              <LabeledDatePickerInput
                label="Due Date"
                name="dueDate"
                onChange={(date: string | Date) => {
                  handleSetDate("dueDate", date);
                }}
                onBlur={handleBlur}
                date={values.dueDate}
                placement="bottom"
                isInvalid={touched.dueDate && !!errors.dueDate}
                errorMessage={errors.dueDate}
                isRequired
                data-testid="due-date-input"
              />
            </InputFieldWrapper>
            <Box pt={4}>
              <Button
                width="100px"
                variant="primary"
                data-testid="create-call-next-button"
                isDisabled={!dirty || !isValid}
                onClick={onOpen}
              >
                Next
              </Button>
            </Box>
            <CreateCallPreviewModal
              isOpen={isOpen}
              onClose={onClose}
              accountId={accountId}
              accountTitle={accountData.accountTitle}
              setAccountId={setAccountId}
            />
          </Stack>
        </form>
      )}
    </>
  );
};

export default CreateCallForm;
